import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import usePullToRefresh from '../hooks/usePullToRefresh';
import PullToRefreshIndicator from '../components/PullToRefreshIndicator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ShortTermAdvances = () => {
  const [advances, setAdvances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newAdvanceOpen, setNewAdvanceOpen] = useState(false);
  const [repaymentOpen, setRepaymentOpen] = useState(false);
  const [selectedAdvance, setSelectedAdvance] = useState(null);

  const [newAdvance, setNewAdvance] = useState({
    employee_name: '',
    amount: '',
    advance_date: new Date().toISOString().split('T')[0]
  });

  const [repayment, setRepayment] = useState({
    repayment_amount: '',
    repayment_date: new Date().toISOString().split('T')[0],
    voucher_number: ''
  });

  useEffect(() => {
    fetchAdvances();
  }, []);

  const fetchAdvances = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/short-term-advances`);
      setAdvances(response.data);
    } catch (error) {
      console.error('Error fetching advances:', error);
      toast.error('Failed to load advances');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleRefresh = useCallback(async () => {
    toast.info('Refreshing...');
    await fetchAdvances();
    toast.success('Data refreshed!');
  }, [fetchAdvances]);

  const { pullDistance, isRefreshing, showIndicator } = usePullToRefresh(handleRefresh);

  const handleCreateAdvance = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/short-term-advances`, {
        ...newAdvance,
        amount: parseFloat(newAdvance.amount)
      });
      toast.success('Advance recorded successfully');
      setNewAdvanceOpen(false);
      setNewAdvance({ employee_name: '', amount: '', advance_date: new Date().toISOString().split('T')[0] });
      fetchAdvances();
    } catch (error) {
      console.error('Error creating advance:', error);
      toast.error('Failed to record advance');
    }
  };

  const handleRepayment = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/short-term-advances/${selectedAdvance.id}/repay`, {
        ...repayment,
        repayment_amount: parseFloat(repayment.repayment_amount)
      });
      toast.success('Repayment recorded successfully');
      setRepaymentOpen(false);
      setRepayment({ repayment_amount: '', repayment_date: new Date().toISOString().split('T')[0], voucher_number: '' });
      setSelectedAdvance(null);
      fetchAdvances();
    } catch (error) {
      console.error('Error recording repayment:', error);
      toast.error('Failed to record repayment');
    }
  };

  const openRepaymentDialog = (advance) => {
    setSelectedAdvance(advance);
    setRepayment({
      repayment_amount: advance.amount.toString(),
      repayment_date: new Date().toISOString().split('T')[0],
      voucher_number: ''
    });
    setRepaymentOpen(true);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full" data-testid="short-term-loading"><div className="text-blue-700">Loading...</div></div>;
  }

  return (
    <div className="animate-in" data-testid="short-term-advances">
      <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={isRefreshing} />
      <div className="mb-6 md:mb-8 flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">Short-term Advances</h1>
          <p className="text-sm md:text-base text-blue-700/80">Manage one-time salary advances</p>
        </div>
        <Dialog open={newAdvanceOpen} onOpenChange={setNewAdvanceOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="new-short-term-advance-button"
              className="bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:shadow-xl hover:shadow-blue-500/50 transition-all active:scale-95 font-semibold px-4 py-2 md:px-6 md:py-3 text-sm md:text-base"
            >
              <Plus size={20} className="mr-2" />
              New Advance
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="new-advance-dialog" className="glass-card max-w-[95vw] md:max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-blue-900">Record New Short-term Advance</DialogTitle>
              <DialogDescription className="text-blue-600">Enter employee details and advance amount</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateAdvance} className="space-y-4">
              <div>
                <Label htmlFor="employee_name" className="text-blue-900">Employee Name</Label>
                <Input
                  id="employee_name"
                  data-testid="input-employee-name"
                  value={newAdvance.employee_name}
                  onChange={(e) => setNewAdvance({ ...newAdvance, employee_name: e.target.value })}
                  required
                  className="border-blue-200 focus:border-blue-500"
                />
              </div>
              <div>
                <Label htmlFor="amount" className="text-blue-900">Amount (₹)</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  data-testid="input-amount"
                  value={newAdvance.amount}
                  onChange={(e) => setNewAdvance({ ...newAdvance, amount: e.target.value })}
                  required
                  className="border-blue-200 focus:border-blue-500"
                />
              </div>
              <div>
                <Label htmlFor="advance_date" className="text-blue-900">Advance Date</Label>
                <Input
                  id="advance_date"
                  type="date"
                  data-testid="input-advance-date"
                  value={newAdvance.advance_date}
                  onChange={(e) => setNewAdvance({ ...newAdvance, advance_date: e.target.value })}
                  required
                  className="border-blue-200 focus:border-blue-500"
                />
              </div>
              <Button type="submit" data-testid="submit-advance" className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:shadow-lg">
                Record Advance
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4">
        {advances.length === 0 ? (
          <div className="glass-card rounded-2xl p-6 text-center text-blue-600" data-testid="no-advances">
            No advances found
          </div>
        ) : (
          advances.map((advance) => (
            <div key={advance.id} data-testid={`advance-card-${advance.id}`} className="glass-card rounded-2xl border border-blue-200 p-5 space-y-3 shadow-lg">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-bold text-blue-900 text-lg">{advance.employee_name}</h3>
                  <p className="text-sm text-blue-600">{advance.advance_date}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  advance.status === 'pending' 
                    ? 'bg-amber-100 text-amber-700 border border-amber-300'
                    : 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                }`}>
                  {advance.status === 'pending' ? 'Pending' : 'Repaid'}
                </span>
              </div>
              
              <div className="pt-3 border-t border-blue-100">
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <p className="text-xs text-blue-600">Amount</p>
                    <p className="font-bold text-blue-900 tabular-nums">₹{advance.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                  </div>
                  {advance.repayment_date && (
                    <div>
                      <p className="text-xs text-blue-600">Repaid</p>
                      <p className="font-bold text-emerald-600 tabular-nums">₹{advance.repayment_amount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                    </div>
                  )}
                </div>
                
                {advance.repayment_date ? (
                  <div className="bg-emerald-50 rounded-lg p-3 text-xs">
                    <p className="text-emerald-700"><span className="font-semibold">Paid on:</span> {advance.repayment_date}</p>
                    <p className="text-emerald-700"><span className="font-semibold">Voucher:</span> {advance.voucher_number}</p>
                  </div>
                ) : (
                  <Button
                    data-testid={`repay-button-mobile-${advance.id}`}
                    onClick={() => openRepaymentDialog(advance)}
                    className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:shadow-lg py-6 text-base font-semibold"
                  >
                    Record Repayment
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block glass-card rounded-2xl shadow-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border-b border-blue-200">
              <tr>
                <th className="text-left text-xs font-semibold text-blue-900 uppercase tracking-wider p-4">Employee</th>
                <th className="text-left text-xs font-semibold text-blue-900 uppercase tracking-wider p-4">Amount</th>
                <th className="text-left text-xs font-semibold text-blue-900 uppercase tracking-wider p-4">Date</th>
                <th className="text-left text-xs font-semibold text-blue-900 uppercase tracking-wider p-4">Status</th>
                <th className="text-left text-xs font-semibold text-blue-900 uppercase tracking-wider p-4">Repayment</th>
                <th className="text-left text-xs font-semibold text-blue-900 uppercase tracking-wider p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {advances.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center p-8 text-blue-600" data-testid="no-advances">No advances found</td>
                </tr>
              ) : (
                advances.map((advance) => (
                  <tr key={advance.id} data-testid={`advance-row-${advance.id}`} className="border-b border-blue-100 hover:bg-blue-50/50 transition-colors">
                    <td className="p-4 text-sm text-blue-900 font-medium">{advance.employee_name}</td>
                    <td className="p-4 text-sm text-blue-900 tabular-nums">₹{advance.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                    <td className="p-4 text-sm text-blue-700">{advance.advance_date}</td>
                    <td className="p-4 text-sm">
                      {advance.status === 'pending' ? (
                        <span className="bg-amber-100 text-amber-700 border border-amber-300 px-2 py-0.5 rounded-full text-xs font-medium">Pending</span>
                      ) : (
                        <span className="bg-emerald-100 text-emerald-700 border border-emerald-300 px-2 py-0.5 rounded-full text-xs font-medium">Repaid</span>
                      )}
                    </td>
                    <td className="p-4 text-sm text-blue-700">
                      {advance.repayment_date ? (
                        <div>
                          <div className="tabular-nums">₹{advance.repayment_amount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                          <div className="text-xs text-blue-600">{advance.repayment_date}</div>
                          <div className="text-xs text-blue-500">Voucher: {advance.voucher_number}</div>
                        </div>
                      ) : (
                        <span className="text-blue-400">-</span>
                      )}
                    </td>
                    <td className="p-4 text-sm">
                      {advance.status === 'pending' && (
                        <Button
                          data-testid={`repay-button-${advance.id}`}
                          size="sm"
                          onClick={() => openRepaymentDialog(advance)}
                          className="bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:shadow-lg text-xs"
                        >
                          Record Repayment
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={repaymentOpen} onOpenChange={setRepaymentOpen}>
        <DialogContent data-testid="repayment-dialog" className="glass-card max-w-[95vw] md:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-blue-900">Record Repayment</DialogTitle>
            <DialogDescription className="text-blue-600">Record the repayment details for this advance</DialogDescription>
          </DialogHeader>
          {selectedAdvance && (
            <form onSubmit={handleRepayment} className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-md border border-blue-200">
                <p className="text-sm text-blue-700">Employee: <span className="font-semibold text-blue-900">{selectedAdvance.employee_name}</span></p>
                <p className="text-sm text-blue-700">Original Amount: <span className="font-semibold text-blue-900">₹{selectedAdvance.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></p>
              </div>
              <div>
                <Label htmlFor="repayment_amount" className="text-blue-900">Repayment Amount (₹)</Label>
                <Input
                  id="repayment_amount"
                  type="number"
                  step="0.01"
                  data-testid="input-repayment-amount"
                  value={repayment.repayment_amount}
                  onChange={(e) => setRepayment({ ...repayment, repayment_amount: e.target.value })}
                  required
                  className="border-blue-200 focus:border-blue-500"
                />
              </div>
              <div>
                <Label htmlFor="repayment_date" className="text-blue-900">Repayment Date</Label>
                <Input
                  id="repayment_date"
                  type="date"
                  data-testid="input-repayment-date"
                  value={repayment.repayment_date}
                  onChange={(e) => setRepayment({ ...repayment, repayment_date: e.target.value })}
                  required
                  className="border-blue-200 focus:border-blue-500"
                />
              </div>
              <div>
                <Label htmlFor="voucher_number" className="text-blue-900">Voucher Number</Label>
                <Input
                  id="voucher_number"
                  data-testid="input-voucher-number"
                  value={repayment.voucher_number}
                  onChange={(e) => setRepayment({ ...repayment, voucher_number: e.target.value })}
                  required
                  className="border-blue-200 focus:border-blue-500"
                />
              </div>
              <Button type="submit" data-testid="submit-repayment" className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:shadow-lg py-6 text-base font-semibold">
                Record Repayment
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShortTermAdvances;